import { ApiError, onSessionLost, readToken, writeToken } from '../api/client.ts';
import { auth, me } from '../api/endpoints.ts';
import type { OtpRequested, Profile } from '../api/types.ts';
import { createStore, useStore } from '../core/store.ts';
import { deviceLang } from '../i18n/index.ts';
import { clearSavedCards } from './payment.ts';
import { removePushSubscription } from './push.ts';

/**
 * Сессия клиента: токен и профиль.
 *
 * Токен лежит в localStorage и переживает перезапуск: каждый повторный вход стоит SMS.
 * Ответ 401 означает отозванную сессию: приложение возвращается в гостя.
 */
export type SessionState = {
  status: 'unknown' | 'signedOut' | 'signedIn';
  profile: Profile | null;
  /** Токен есть, но проверить его не удалось: сервер не ответил. */
  restoreFailed: boolean;
  /** Растёт каждый раз, когда сервер отверг токен; выход из профиля не считается. */
  revocations: number;
  /** Выбор языка открыт касаниями в профиле; между запусками не хранится. */
  languageUnlocked: boolean;
};

export const sessionStore = createStore<SessionState>({
  status: 'unknown',
  profile: null,
  restoreFailed: false,
  revocations: 0,
  languageUnlocked: false,
});

export function useSession(): SessionState {
  return useStore(sessionStore);
}

export function isSignedIn(): boolean {
  return sessionStore.get().profile !== null;
}

/**
 * Выбор языка руками спрятан, пока язык браузера — один из наших: приложение и так
 * открывается на нём. Профиль открывает выбор десятью касаниями заголовка.
 */
export function showsLanguagePicker(state: SessionState): boolean {
  return deviceLang() === null || state.languageUnlocked;
}

export function unlockLanguagePicker(): void {
  sessionStore.set((state) => ({ ...state, languageUnlocked: true }));
}

function signedIn(profile: Profile): void {
  sessionStore.set((state) => ({ ...state, status: 'signedIn', profile, restoreFailed: false }));
}

/** Сессия недействительна: токен стирается, приложение остаётся витриной гостя. */
function forgetToken(revoked = false): void {
  writeToken(null);
  sessionStore.set((state) => ({
    ...state,
    status: 'signedOut',
    profile: null,
    revocations: state.revocations + (revoked ? 1 : 0),
  }));
}

onSessionLost(() => {
  if (sessionStore.get().status !== 'signedOut') forgetToken(true);
});

/** Стартовая проверка: есть ли живой токен. */
export async function restoreSession(): Promise<void> {
  if (readToken() === null) {
    sessionStore.set((state) => ({ ...state, status: 'signedOut', profile: null }));
    return;
  }

  sessionStore.set((state) => ({ ...state, restoreFailed: false }));
  try {
    signedIn(await me.get());
  } catch (error) {
    // 401 уже обработан `onSessionLost`. Сервер не ответил, а токен живой: в гостя не
    // отправляем — повторный вход стоит SMS, а причина в обрыве связи.
    if (error instanceof ApiError && error.isUnauthorized) return;
    sessionStore.set((state) => ({ ...state, restoreFailed: true }));
  }
}

export function requestCode(phone: string, bySms = false): Promise<OtpRequested> {
  return auth.requestCode(phone, bySms);
}

export async function verifyCode(phone: string, code: string): Promise<void> {
  const verified = await auth.verifyCode(phone, code);
  writeToken(verified.token);
  signedIn(await me.get());
}

/** Готовый токен снаружи (мост демо-стенда). */
export async function adoptToken(token: string): Promise<void> {
  writeToken(token);
  await restoreSession();
}

async function reloadProfile(): Promise<void> {
  const fresh = await me.get().catch(() => null);
  if (fresh && sessionStore.get().status === 'signedIn') signedIn(fresh);
}

/** Имя необязательно: пустое сервер хранит как отсутствующее. */
export async function setName(name: string): Promise<void> {
  await me.update({ name });
  await reloadProfile();
}

/** E-mail для писем об оплате и чеков; пустая строка убирает его из профиля. */
export async function setEmail(email: string): Promise<void> {
  await me.update({ email });
  await reloadProfile();
}

/** Дата рождения `YYYY-MM-DD` для товаров 18+; null убирает её из профиля. */
export async function setBirthDate(birthDate: string | null): Promise<void> {
  await me.update({ birthDate });
  await reloadProfile();
}

/** Фото уже уменьшено в браузере: сервер кладёт его в хранилище и ставит в профиль. */
export async function uploadAvatar(jpeg: Blob): Promise<void> {
  await me.uploadAvatar(jpeg);
  await reloadProfile();
}

export async function removeAvatar(): Promise<void> {
  await me.update({ avatarUrl: null });
  await reloadProfile();
}

export async function signOut(): Promise<void> {
  // Подписка на push снимается до выхода: после него сессии уже нет.
  await removePushSubscription();
  // Отзыв сессии на сервере может не пройти, локально выйти всё равно надо.
  await auth.logout().catch(() => undefined);
  forgetToken();
  clearSavedCards();
}

/** Удаление аккаунта: сервер стирает личные данные и отзывает сессии. */
export async function deleteAccount(): Promise<void> {
  await removePushSubscription();
  await me.remove();
  forgetToken();
}
